import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ExternalLink,
  Info,
  Loader2,
  Save,
} from "lucide-react";
import toast from "react-hot-toast";
import { getDocData, setDocData } from "../../Firebase/Firebase.utils";
import { humanizeKey } from "../utils/humanize";
import { slugify } from "../utils/slugify";
import { resolveUniqueSlug } from "../utils/slugUniqueness";
import FieldValue from "../components/FieldValue";
import BuildingPhotoUpload from "../components/BuildingPhotoUpload";
import IncompleteWarning from "../components/IncompleteWarning";
import { getBuildingMissingFields } from "../utils/incompleteChecks";
import { revalidateFor } from "../utils/revalidatePublic";

// External link to Raven for editing read-only fields. Buildings live
// in Raven's commercial-leasing masterlist module.
const RAVEN_URL =
  process.env.REACT_APP_RAVEN_URL || "https://raven.leechiu.app";

const META_TITLE_MAX = 60;
const META_DESC_MAX = 160;

const READ_ONLY_SECTIONS = [
  {
    title: "Identity & location",
    description:
      "Canonical building details. Edit in Raven's Commercial Leasing > Masterlist.",
    fields: [
      "buildingName",
      "address",
      "city",
      "businessDistrict",
      "googleMapsPin",
    ],
  },
  {
    title: "Build details",
    description: "Developer info, certifications, and completion.",
    fields: [
      "developer",
      "yearConstructed",
      "certifications",
      "buildingGrade",
    ],
  },
  {
    title: "Floor specifications",
    description: "Shown on the building detail page.",
    fields: [
      "totalFloors",
      "floorPlate",
      "grossLeasableArea",
      "netUseableArea",
      "floorEfficiency",
      "density",
      "ceilingHeight",
    ],
  },
  {
    title: "Building systems",
    description: "Technical operating details.",
    fields: [
      "acSystem",
      "backupPower",
      "elevators",
      "telcoProviders",
      "parkingAllocation",
      "twentyFourSevenOperations",
    ],
  },
  {
    title: "Team",
    description: "Listed on the detail page next to the inquiry form.",
    fields: ["teamMembers"],
  },
];

const EMPTY_FORM = {
  description: "",
  metaTitle: "",
  metaDescription: "",
  slug: "",
  primaryPhotoUrl: "",
  pinned: false,
  showOnWebsite: false,
};

export default function OfficeBuildingDetail() {
  const { buildingId } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [initial, setInitial] = useState(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!buildingId) return;
    let active = true;
    (async () => {
      try {
        const data = await getDocData("masterlist", buildingId);
        if (!active) return;
        if (!data || data === false || data instanceof Error) {
          toast.error("Building not found.");
          navigate("/cms/offices", { replace: true });
          return;
        }
        setDoc(data);
        // Seed showOnWebsite from the legacy public gate (exclusive OR
        // webStatus=published) when the field hasn't been set yet, so
        // the toggle initial state matches what the visitor sees.
        const legacyVisible =
          data.exclusive === "Yes" || data.webStatus === "published";
        const next = {
          description: data.description ?? "",
          metaTitle: data.metaTitle ?? "",
          metaDescription: data.metaDescription ?? "",
          slug: data.slug ?? "",
          primaryPhotoUrl: data.primaryPhotoUrl ?? "",
          pinned: data.pinned === true,
          showOnWebsite:
            typeof data.showOnWebsite === "boolean"
              ? data.showOnWebsite
              : legacyVisible,
        };
        setForm(next);
        setInitial(next);
        setSlugTouched(!!data.slug);

        // First-touch stamp — powers the "New (not opened yet)" filter
        // on /cms/offices. Fire-and-forget; not part of the visible
        // dirty-state or Save flow.
        if (!data.cmsOpenedAt) {
          setDocData("masterlist", buildingId, {
            cmsOpenedAt: new Date().toISOString(),
          }).catch((err) =>
            console.warn("[OfficeBuildingDetail] cmsOpenedAt stamp failed:", err),
          );
        }
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [buildingId, navigate]);

  // Auto-derive slug from buildingName (read-only, sourced from Raven)
  // when the user hasn't manually touched the slug field.
  useEffect(() => {
    if (slugTouched) return;
    const seed = doc?.buildingName;
    if (!seed) return;
    setForm((p) => ({ ...p, slug: slugify(seed) }));
  }, [doc?.buildingName, slugTouched]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const isDirty = useMemo(
    () => Object.keys(EMPTY_FORM).some((k) => form[k] !== initial[k]),
    [form, initial],
  );

  // Missing-requirement check for the IncompleteWarning banner.
  // Merges the live form's editable fields (photo + description) over
  // the persisted doc so dismissing a warning line doesn't require a
  // Save. Everything else (buildingName, address, city) is Raven-owned
  // and read from the persisted doc.
  const missingRequirements = useMemo(() => {
    if (!doc) return [];
    return getBuildingMissingFields({
      ...doc,
      primaryPhotoUrl: form.primaryPhotoUrl || doc.primaryPhotoUrl,
      description: form.description || doc.description,
    });
  }, [doc, form.primaryPhotoUrl, form.description]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const finalSlug = await resolveUniqueSlug({
        collectionName: "masterlist",
        desiredSlug: form.slug,
        fallbackTitle: doc?.buildingName,
        ignoreId: buildingId,
      });

      const payload = {
        description: form.description,
        metaTitle: form.metaTitle.trim(),
        metaDescription: form.metaDescription.trim(),
        slug: finalSlug,
        primaryPhotoUrl: form.primaryPhotoUrl,
        pinned: form.pinned,
        showOnWebsite: form.showOnWebsite,
        updatedAt: new Date().toISOString(),
      };
      await setDocData("masterlist", buildingId, payload);
      revalidateFor("masterlist");

      const next = { ...form, slug: finalSlug };
      setForm(next);
      setInitial(next);
      setDoc((prev) => ({ ...(prev || {}), ...payload }));
      toast.success("Building saved.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save building.");
    } finally {
      setIsSaving(false);
    }
  };

  const title = doc?.buildingName || "Untitled building";

  const saveDisabled = !isDirty || isSaving;
  const titleLen = form.metaTitle.length;
  const descLen = form.metaDescription.length;

  // Browser back preserves the manager's URL params
  // (?filter=incomplete&q=…) so the visitor returns to the same list
  // state. Fall back to a plain redirect if there's no session history.
  const goBackToList = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/cms/offices");
    }
  };

  return (
    <div className="pb-24">
      <div className="mb-4">
        <button
          type="button"
          onClick={goBackToList}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-leechiuBlue"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Offices
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div className="min-w-0">
          <div className="cms-eyebrow mb-1.5">Editing building</div>
          <h1 className="line-clamp-2">{title}</h1>
          <div className="text-xs font-mono text-gray-400 mt-1">
            {buildingId}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading building…
        </div>
      ) : (
        <>
        <IncompleteWarning missing={missingRequirements} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-2xl ring-1 ring-gray-100 overflow-hidden">
              <BuildingPhotoUpload
                buildingId={buildingId}
                value={form.primaryPhotoUrl}
                onChange={(url) => setField("primaryPhotoUrl", url)}
              />
              <div className="p-4 space-y-1.5 text-xs text-gray-600 border-t border-gray-100">
                {doc.address ? (
                  <div>
                    <span className="font-medium text-gray-700">Address: </span>
                    {doc.address}
                  </div>
                ) : null}
                {doc.city ? (
                  <div>
                    <span className="font-medium text-gray-700">City: </span>
                    {doc.city}
                  </div>
                ) : null}
                {doc.businessDistrict ? (
                  <div>
                    <span className="font-medium text-gray-700">
                      District:{" "}
                    </span>
                    {doc.businessDistrict}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Editable: Website visibility — same slot the property
                listing detail uses so the two CMS pages stay aligned. */}
            <section className="bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm overflow-hidden">
              <header className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-leechiuBlue m-0">
                  Website visibility
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Master control for whether this building appears on the
                  public site. Overrides the exclusive / webStatus rule.
                </p>
              </header>
              <div className="p-5">
                <ShowOnWebsiteToggle
                  value={form.showOnWebsite}
                  onChange={(v) => setField("showOnWebsite", v)}
                />
              </div>
            </section>
          </div>

          {/* Main column — editable cards, then read-only sections */}
          <div className="lg:col-span-2 space-y-6">
            {/* Editable: Description */}
            <section className="bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm overflow-hidden">
              <header className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-leechiuBlue m-0">
                  Description
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Body copy shown in the "Property Overview" section on
                  the public building detail page.
                </p>
              </header>
              <div className="p-5">
                <TextArea
                  label="Building description"
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  rows={6}
                  placeholder="Describe the building's location, amenities, and what makes it stand out."
                />
              </div>
            </section>

            {/* Editable: Promotion */}
            <section className="bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm overflow-hidden">
              <header className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-leechiuBlue m-0">
                  Promotion
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Boost this building's placement on the public website.
                </p>
              </header>
              <div className="p-5">
                <PinToggle
                  value={form.pinned}
                  onChange={(v) => setField("pinned", v)}
                />
              </div>
            </section>

            {/* Editable: SEO */}
            <section className="bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm overflow-hidden">
              <header className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-leechiuBlue m-0">
                  Building page SEO
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Used by search engines and social-media previews. The
                  page URL drives the public /offices/&lt;url&gt; route.
                </p>
              </header>
              <div className="p-5 space-y-4">
                <div>
                  <TextField
                    label="Page URL"
                    value={form.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setField("slug", e.target.value);
                    }}
                    onBlur={(e) => {
                      const formatted = slugify(e.target.value);
                      if (formatted !== e.target.value) {
                        setField("slug", formatted);
                      }
                    }}
                    placeholder="auto-generated-from-building-name"
                    hint="Paste any word or phrase — it will be formatted to a URL-friendly slug on blur. Leave blank to auto-generate from the building name. Uniqueness across all buildings is checked on save."
                  />
                </div>
                <div>
                  <TextField
                    label="SEO title"
                    value={form.metaTitle}
                    onChange={(e) => setField("metaTitle", e.target.value)}
                    placeholder="Shown in browser tabs and search results"
                    maxLength={META_TITLE_MAX + 20}
                  />
                  <CounterHint
                    length={titleLen}
                    max={META_TITLE_MAX}
                    recommendation="50–60 characters"
                  />
                </div>
                <div>
                  <TextArea
                    label="Meta description"
                    value={form.metaDescription}
                    onChange={(e) =>
                      setField("metaDescription", e.target.value)
                    }
                    rows={3}
                    placeholder="Brief summary for search results and social shares"
                    maxLength={META_DESC_MAX + 40}
                  />
                  <CounterHint
                    length={descLen}
                    max={META_DESC_MAX}
                    recommendation="150–160 characters"
                  />
                </div>
              </div>
            </section>

            {/* "Update in Raven" banner */}
            <div className="rounded-2xl bg-gradient-to-br from-leechiuBlue/5 via-white to-leechiuOrange/5 ring-1 ring-leechiuBlue/10 p-5 flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-leechiuBlue/10 text-leechiuBlue flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-leechiuBlue m-0">
                  To edit the fields below, update the building in Raven
                </h4>
                <p className="text-xs text-gray-600 mt-1">
                  Building name, address, specs, and team assignments
                  flow into this record from the source-of-truth in
                  Raven's Commercial Leasing &gt; Masterlist. Make those
                  changes there.
                </p>
                <a
                  href={`${RAVEN_URL}/commercial-leasing/masterlist`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg bg-leechiuBlue text-white text-xs font-medium hover:opacity-90"
                >
                  Open in Raven
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            {READ_ONLY_SECTIONS.map((section) => (
              <FieldSection
                key={section.title}
                title={section.title}
                description={section.description}
                doc={doc}
                fields={section.fields}
              />
            ))}
          </div>
        </div>
        </>
      )}

      {/* Sticky save bar */}
      {!isLoading ? (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <div className="text-xs text-gray-500">
              {isDirty ? (
                <span className="text-leechiuOrange font-medium">
                  Unsaved changes
                </span>
              ) : (
                "All changes saved"
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setForm(initial)}
                disabled={!isDirty || isSaving}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm disabled:opacity-50"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saveDisabled}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-leechiuOrange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isSaving ? "Saving…" : "Save building"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FieldSection({ title, description, doc, fields }) {
  const rows = fields.map((field) => ({
    field,
    label: humanizeKey(field),
    value: doc[field],
    exists: Object.prototype.hasOwnProperty.call(doc, field),
  }));

  return (
    <section className="bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm overflow-hidden">
      <header className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-leechiuBlue m-0">{title}</h3>
        {description ? (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        ) : null}
      </header>
      <dl className="divide-y divide-gray-100">
        {rows.map(({ field, label, value, exists }) => (
          <div
            key={field}
            className="grid grid-cols-1 sm:grid-cols-[11rem_1fr] gap-1 sm:gap-4 px-5 py-3"
          >
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 sm:pt-0.5">
              {label}
              {!exists ? (
                <span className="ml-1 normal-case font-normal text-[10px] text-gray-300">
                  (not set on this doc)
                </span>
              ) : null}
            </dt>
            <dd className="text-sm text-gray-700 min-w-0">
              <FieldValue value={value} />
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function TextField({ label, hint, ...rest }) {
  return (
    <label className="block">
      <span className="cms-eyebrow block mb-1.5 text-gray-600">{label}</span>
      <input
        {...rest}
        className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white focus:ring-2 focus:ring-leechiuOrange focus:border-transparent outline-none text-sm"
      />
      {hint ? (
        <span className="block text-xs text-gray-400 mt-1">{hint}</span>
      ) : null}
    </label>
  );
}

function TextArea({ label, hint, rows = 3, ...rest }) {
  return (
    <label className="block">
      <span className="cms-eyebrow block mb-1.5 text-gray-600">{label}</span>
      <textarea
        rows={rows}
        {...rest}
        className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white focus:ring-2 focus:ring-leechiuOrange focus:border-transparent outline-none text-sm"
      />
      {hint ? (
        <span className="block text-xs text-gray-400 mt-1">{hint}</span>
      ) : null}
    </label>
  );
}

function ShowOnWebsiteToggle({ value, onChange }) {
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-xl ring-1 transition-colors ${
        value
          ? "bg-emerald-50 ring-emerald-200"
          : "bg-amber-50 ring-amber-200"
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-leechiuBlue">
          Show on website
        </div>
        <div className="text-xs text-gray-600 mt-0.5">
          {value
            ? "This building will appear on /offices regardless of exclusive / webStatus."
            : "This building is hidden from /offices regardless of exclusive / webStatus."}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        role="switch"
        aria-checked={value}
        aria-label="Show on website"
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
          value ? "bg-emerald-500" : "bg-gray-300"
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition mt-0.5 ${
            value ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}

function PinToggle({ value, onChange }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 ring-1 ring-gray-100">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-leechiuBlue">
          Pin to top of offices
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          Pinned buildings appear first on /offices. Use this for
          featured properties.
        </div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        role="switch"
        aria-checked={value}
        aria-label="Pin to top of offices"
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
          value ? "bg-leechiuOrange" : "bg-gray-300"
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition mt-0.5 ${
            value ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}

function CounterHint({ length, max, recommendation }) {
  const over = length > max;
  return (
    <div className="mt-1 flex items-center justify-between text-[11px]">
      <span className="text-gray-400">Recommended: {recommendation}</span>
      <span className={over ? "text-red-500 font-medium" : "text-gray-400"}>
        {length}/{max}
      </span>
    </div>
  );
}
